import { useState } from 'react'
import { Shield } from 'lucide-react'
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
  Button,
  Label,
  Avatar,
  AvatarFallback,
} from '@/components/ui'
import type { FamilyMember, FamilyRole, UpdateMemberRequest } from '@/types'

interface EditMemberModalProps {
  open: boolean
  member: FamilyMember | null
  onClose: () => void
  onUpdate: (args: { memberId: string; data: UpdateMemberRequest }) => Promise<unknown>
  isUpdating: boolean
}

const ROLE_OPTIONS: { value: FamilyRole; label: string; description: string }[] = [
  {
    value: 'PARENT',
    label: 'Parent',
    description: 'Family member being monitored',
  },
  {
    value: 'CAREGIVER',
    label: 'Caregiver',
    description: 'Family member who monitors others',
  },
  {
    value: 'VIEWER',
    label: 'Viewer',
    description: 'Read-only access',
  },
]

export function EditMemberModal({
  open,
  member,
  onClose,
  onUpdate,
  isUpdating,
}: EditMemberModalProps) {
  // Initialize state from member props - reset when member changes
  const [role, setRole] = useState<FamilyRole>(member?.role ?? 'PARENT')
  const [error, setError] = useState('')

  // Reset state when member changes (e.g., when opening modal for different member)
  const currentMemberId = member?.id
  const [prevMemberId, setPrevMemberId] = useState(currentMemberId)
  if (currentMemberId !== prevMemberId) {
    setPrevMemberId(currentMemberId)
    if (member) {
      setRole(member.role)
      setError('')
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')

    if (!member) return

    try {
      await onUpdate({
        memberId: member.id,
        data: {
          role,
        },
      })
      onClose()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update member')
    }
  }


  if (!member) return null

  const initials = member.user.full_name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2)

  // Parent role doesn't need permission selection


  // Check if anything changed
  const hasChanges =
    role !== member.role

  return (
    <Sheet open={open} onOpenChange={onClose}>
      <SheetContent>
        <SheetHeader>
          <SheetTitle>Edit Member</SheetTitle>
          <SheetDescription>
            Update the role for this family member.
          </SheetDescription>
        </SheetHeader>

        {/* Member info */}
        <div className="mt-6 flex items-center gap-3 rounded-lg bg-gray-50 p-4">
          <Avatar className="h-12 w-12">
            <AvatarFallback className="bg-gray-200">{initials}</AvatarFallback>
          </Avatar>
          <div>
            <div className="font-medium text-gray-900">{member.user.full_name}</div>
            <div className="text-sm text-gray-500">{member.user.email}</div>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="mt-6 space-y-6">
          {/* Role selection */}
          <div>
            <Label>Role</Label>
            <div className="mt-2 space-y-2">
              {ROLE_OPTIONS.map((option) => (
                <label
                  key={option.value}
                  className={`flex cursor-pointer items-start gap-3 rounded-lg border p-3 transition-colors ${
                    role === option.value
                      ? 'border-primary-500 bg-primary-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <input
                    type="radio"
                    name="role"
                    value={option.value}
                    checked={role === option.value}
                    onChange={() => setRole(option.value)}
                    className="mt-0.5"
                  />
                  <div>
                    <div className="font-medium text-gray-900">{option.label}</div>
                    <div className="text-sm text-gray-500">{option.description}</div>
                  </div>
                </label>
              ))}
            </div>
          </div>

          <p className="text-sm text-gray-500">Access is determined by the selected role. Viewers can view health; caregivers can view health and finances and manage alerts; parents manage their own data.</p>

          {/* Error display */}
          {error && (
            <div className="rounded-md bg-error-50 p-3 text-sm text-error-700">
              {error}
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-3 pt-4">
            <Button type="button" variant="outline" onClick={onClose} className="flex-1">
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isUpdating || !hasChanges}
              className="flex-1"
            >
              <Shield className="mr-2 h-4 w-4" />
              {isUpdating ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        </form>
      </SheetContent>
    </Sheet>
  )
}
